import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error?: Error;
}

/** Last-resort crash screen so a runtime error never shows a blank page. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {};

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Hook point for Sentry/Crashlytics in production.
    console.error('[ZoneIn] Uncaught error:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 dark:bg-slate-950 p-8 text-center">
          <span className="text-4xl">😵</span>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Something went wrong</h1>
          <p className="max-w-sm text-sm text-slate-500 dark:text-slate-400">
            The app hit an unexpected error. Your data is saved on this device —
            reloading usually fixes it.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-xl bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-lg active:scale-95"
          >
            Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
